import {action, observable, computed} from 'mobx';
import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
import {requireImage} from '../../utils/utils';
import {SURVEY_ANSWER_MAX_LENGTH} from '@techsee/techsee-common/lib/constants/room.constants';
import {SURVEY_CONSTANTS} from '../../../states/dashboard/dashboard.settings';
import {ITranslate} from '../../services/LocalizationService';
import {IAgentSurveyService, Question, IAnswer} from '@techsee/techsee-client-infra/lib/services/AgentSurveyService';
import {SurveyTypes, SurveyStatuses, AnswerTypes} from '@techsee/techsee-common/lib/constants/agent-surveys.constants';
import {IRoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';

export enum SurveyModalEvents {
    SUBMIT = 'SUBMIT',
    CLOSE = 'CLOSE',
    SURVEY_COMPLETED = 'SURVEY_COMPLETED',
    SURVEY_SKIPPED = 'SURVEY_SKIPPED'
}

// Extend Question from service to add UI state and _id for tracking
export interface SurveyQuestion extends Question {
    _id: string;
    answer?: string | boolean;
}

export class SurveyModalController extends TechseeBaseController {
    @observable isVisible: boolean = false;
    @observable failure: boolean = false;
    @observable questions: SurveyQuestion[] = [];

    public readonly requireImage: (imageName: string, useNewInterface?: boolean) => string;
    public readonly SURVEY_ANSWER_MAX_LENGTH: number = SURVEY_ANSWER_MAX_LENGTH;
    public readonly ANSWER_TYPES = SURVEY_CONSTANTS.ANSWER_TYPES;

    private _isFillingSurvey: boolean = false;

    constructor(
        private agentSurveyService: IAgentSurveyService,
        private accountData: any,
        private roomData: any,
        private localeHelper: any,
        private roomService: IRoomService
    ) {
        super();
        this.requireImage = requireImage;
    }

    @computed
    get isFormValid(): boolean {
        return this.questions.every((question) => {
            if (question.answerType === AnswerTypes.open) {
                return question.answer !== undefined && question.answer !== '';
            }
            return question.answer !== undefined;
        });
    }

    @action
    async show(): Promise<void> {
        const settings = this.accountData.settings;
        const protectedSettings = this.accountData.protectedSettings;
        const dir = this.localeHelper.isRTL() ? 'rtl' : 'ltr';

        // Check if surveys are enabled
        if (protectedSettings?.feedbackSurveys && !settings?.enableSurveys) {
            this.emit(SurveyModalEvents.SURVEY_SKIPPED);
            return;
        }

        if (this._isFillingSurvey) {
            return;
        }
        this._isFillingSurvey = true;

        try {
            // Get room duration
            const duration: any = await this.roomService.getRoomDuration(this.roomData._id);
            const now = new Date();
            const isFailure = duration < settings.failureSurveyThreshold;

            // Check if failure surveys are enabled
            if (isFailure && !settings.enableFailureSurveys) {
                this.emit(SurveyModalEvents.SURVEY_SKIPPED);
                return;
            }

            // Check survey frequency for non-failure surveys
            if (!isFailure && Math.random() > settings.surveyFrequency / 100) {
                this.emit(SurveyModalEvents.SURVEY_SKIPPED);
                return;
            }

            const userId = this.roomData.userId;
            const surveyType = isFailure ? SurveyTypes.failure : SurveyTypes.quality;

            // Create survey
            const surveyResponse = await this.agentSurveyService.createSurvey(
                this.roomData._id,
                typeof userId === 'string' ? userId : userId._id,
                now,
                surveyType
            );
            const surveyId = surveyResponse._id;

            // Get questions
            const questions = await this.agentSurveyService.getQuestions(this.accountData._id, surveyType);

            // Show modal and wait for user response
            const answers = await this.showSurvey(isFailure, questions);

            // Update survey with results
            const status = answers ? SurveyStatuses.done : SurveyStatuses.ignored;

            await this.agentSurveyService.endSurvey(surveyId, status, answers || ([] as IAnswer[]));

            if (answers) {
                this.emit(SurveyModalEvents.SURVEY_COMPLETED, this.translate('DASHBOARD.SURVEY.THANK_YOU'));
            } else {
                this.emit(SurveyModalEvents.SURVEY_SKIPPED);
            }
        } catch (error) {
            this.emit(SurveyModalEvents.SURVEY_SKIPPED);
        } finally {
            this._isFillingSurvey = false;
        }
    }

    @action
    private showSurvey = (failure: boolean, questions: Question[]): Promise<IAnswer[] | false> => {
        return new Promise((resolve) => {
            const onSubmit = (answers: IAnswer[]): void => {
                this.off(SurveyModalEvents.SUBMIT, onSubmit);
                this.off(SurveyModalEvents.CLOSE, onClose);
                resolve(answers);
            };

            const onClose = (): void => {
                this.off(SurveyModalEvents.SUBMIT, onSubmit);
                this.off(SurveyModalEvents.CLOSE, onClose);
                resolve(false);
            };

            this.on(SurveyModalEvents.SUBMIT, onSubmit);
            this.on(SurveyModalEvents.CLOSE, onClose);

            // Initialize questions with default answers and _id
            const initializedQuestions: SurveyQuestion[] = questions.map((question, index) => {
                const q: SurveyQuestion = {
                    ...question,
                    _id: `question-${index}`,
                    answer: undefined
                };
                if (q.answerType === AnswerTypes.open) {
                    q.answer = '';
                } else if (q.answerType === AnswerTypes.select && q.options && q.options.length > 0) {
                    q.answer = q.options[0];
                }
                return q;
            });

            this.failure = failure;
            this.questions = initializedQuestions;
            this.isVisible = true;
        });
    };

    @action
    submit = (): void => {
        if (!this.isFormValid) {
            return;
        }

        const answers: IAnswer[] = this.questions.map((question) => ({
            question: question.question,
            answerType: question.answerType,
            answer: question.answer
        }));

        this.isVisible = false;
        this.questions = [];

        this.emit(SurveyModalEvents.SUBMIT, answers);
    };

    @action
    close = (): void => {
        this.isVisible = false;
        this.questions = [];

        this.emit(SurveyModalEvents.CLOSE);
    };

    @action
    updateQuestionAnswer = (questionId: string, answer: string | boolean): void => {
        const question = this.questions.find((q) => q._id === questionId);
        if (question) {
            question.answer = answer;
        }
    };
}
